import type { PackSubmissionStatus } from '@planuze/admin-contracts';
import { Button, toast } from '@planuze/ui';
import { useState } from 'react';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import {
  type SubmissionAction,
  usePackSubmissionAction,
} from '../../hooks/use-pack-submissions.js';
import { ReasonDialog } from '../reason-dialog.js';
import { availableActions } from './pack-submission-status.js';
import { ACTION_CONFIG, actionVariant, type DialogAction } from './submission-action-config.js';

interface Props {
  readonly submissionId: string;
  readonly status: PackSubmissionStatus;
}

/* Barra de ações da tela de detalhe (ADR-0462) — reusa a state-machine (`availableActions`),
   a mutation (`usePackSubmissionAction`, que invalida lista E detalhe) e o `ReasonDialog`.
   `start-review`/`reopen` disparam direto; approve/reject/request-changes abrem o diálogo.
   `title`/`description`/`confirmLabel`/`reasonLabel` vêm do content bilíngue; `destructive`/
   `validate` (lógica, não texto) continuam vindo do `ACTION_CONFIG` congelado — ele também é
   indexado direto por `routes/pack-submissions-list-page.tsx` (fora do escopo desta migração). */
export function SubmissionActionsBar({ submissionId, status }: Props) {
  const copy = usePackSubmissionsContent();
  const action = usePackSubmissionAction();
  const [pending, setPending] = useState<DialogAction | null>(null);
  const actions = availableActions(status);
  const staticConfig = pending !== null ? ACTION_CONFIG[pending] : null;
  const textConfig = pending !== null ? copy.actionConfig[pending] : null;

  const handle = (act: SubmissionAction): void => {
    if (act === 'start-review' || act === 'reopen') {
      action.mutate(
        { id: submissionId, action: act, reason: '' },
        {
          onSuccess: () =>
            toast.success(
              act === 'reopen' ? copy.actionsBar.reopenedToast : copy.actionsBar.reviewStartedToast,
            ),
          onError: () => toast.error(copy.actionsBar.actionError),
        },
      );
      return;
    }
    setPending(act);
  };

  if (actions.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions.map((act) => (
          <Button
            key={act}
            size="sm"
            variant={actionVariant(act)}
            loading={action.isPending && (act === 'start-review' || act === 'reopen')}
            onClick={() => handle(act)}
          >
            {copy.actionLabel[act]}
          </Button>
        ))}
      </div>
      {pending !== null && staticConfig !== null && textConfig !== null && (
        <ReasonDialog
          open
          onClose={() => setPending(null)}
          title={textConfig.title}
          description={textConfig.description}
          confirmLabel={textConfig.confirmLabel ?? copy.actionLabel[pending]}
          reasonLabel={textConfig.reasonLabel}
          destructive={staticConfig.destructive}
          pending={action.isPending}
          validate={staticConfig.validate}
          onConfirm={(reason) => {
            action.mutate(
              { id: submissionId, action: pending, reason },
              {
                onSuccess: () => {
                  toast.success(copy.actionsBar.submissionUpdatedToast);
                  setPending(null);
                },
                onError: () => toast.error(copy.actionsBar.actionError),
              },
            );
          }}
        />
      )}
    </>
  );
}
